import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchGameQuestions, getGameTypeFromId } from '../utils/gameUtils';

const PercentagePower = () => {
  const navigate = useNavigate();
  const [score, setScore] = useState(0);
  const [question, setQuestion] = useState(null);
  const [userAnswer, setUserAnswer] = useState('');
  const [feedback, setFeedback] = useState('');
  const [problemsSolved, setProblemsSolved] = useState(0);
  const [questionPool, setQuestionPool] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  // Load questions from API on component mount
  useEffect(() => {
    const loadQuestions = async () => {
      try {
        const gameType = getGameTypeFromId('percentage-power');
        const questions = await fetchGameQuestions(5, gameType, 30); // Load 30 questions for the game
        setQuestionPool(questions);
        console.log('Loaded questions for Percentage Power:', questions.length);
      } catch (error) {
        console.error('Failed to load questions:', error);
      }
    };
    
    loadQuestions();
  }, []);

  const generateQuestion = () => {
    if (questionPool.length === 0) {
      // Fallback to hardcoded questions if API fails
      const types = [
        { q: "What is 25% of 80?", a: 20 },
        { q: "What is 50% of 120?", a: 60 },
        { q: "What is 10% of 250?", a: 25 },
        { q: "What is 75% of 40?", a: 30 },
        { q: "Convert 0.25 to percentage:", a: 25 },
        { q: "Convert 0.5 to percentage:", a: 50 },
        { q: "What is 20% of 150?", a: 30 },
        { q: "What is 30% of 200?", a: 60 },
        { q: "What is 15% of 200?", a: 30 },
        { q: "What is 40% of 150?", a: 60 },
        { q: "Convert 0.75 to percentage:", a: 75 },
        { q: "Convert 0.1 to percentage:", a: 10 },
        { q: "What is 5% of 400?", a: 20 },
        { q: "What is 60% of 50?", a: 30 },
        { q: "What is 80% of 125?", a: 100 },
        { q: "Convert 1.5 to percentage:", a: 150 },
      ];
      const q = types[Math.floor(Math.random() * types.length)];
      setQuestion(q);
    } else {
      // Use questions from the pool
      const questionData = questionPool[currentQuestionIndex % questionPool.length];
      setCurrentQuestionIndex(prev => prev + 1);
      
      // Convert API question format to game format
      const correctAnswer = questionData.choices[questionData.answer];
      
      // Extract numeric answer (remove % symbol if present)
      let numericAnswer = parseFloat(correctAnswer.replace('%', '').replace('$', ''));
      
      setQuestion({
        q: questionData.question,
        a: numericAnswer,
        questionData: questionData
      });
    }
    
    setUserAnswer('');
    setFeedback('');
  };

  useEffect(() => {
    generateQuestion();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!question) return;

    const answer = parseFloat(userAnswer);
    if (answer === question.a) {
      setScore(score + 15);
      setProblemsSolved(problemsSolved + 1);
      setFeedback('✅ Correct!');
      setTimeout(generateQuestion, 1500);
    } else {
      setFeedback(`❌ Wrong! Answer: ${question.a}`);
      setTimeout(generateQuestion, 2500);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-500 via-teal-500 to-cyan-500 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 mb-6 text-white">
          <div className="flex justify-between items-center">
            <button onClick={() => navigate('/games')} className="hover:text-yellow-300">← Back</button>
            <h1 className="text-3xl font-bold">📊 Percentage Power</h1>
            <div className="text-2xl font-bold">{score} pts</div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <div className="text-6xl mb-4">%</div>
            <h2 className="text-2xl font-bold text-gray-800">Master Percentages!</h2>
            <p className="text-gray-600">Problems Solved: {problemsSolved}</p>
          </div>

          {question && (
            <div className="text-center">
              <div className="text-2xl font-bold text-green-700 mb-6">{question.q}</div>
              <form onSubmit={handleSubmit} className="max-w-md mx-auto">
                <input
                  type="number"
                  step="0.01"
                  value={userAnswer}
                  onChange={(e) => setUserAnswer(e.target.value)}
                  className="w-full text-4xl text-center border-4 border-green-300 rounded-lg p-4 mb-4 focus:border-green-500 focus:outline-none"
                  placeholder="?"
                  autoFocus
                />
                <button type="submit" className="w-full bg-green-600 text-white text-xl font-bold py-4 rounded-lg hover:bg-green-700">
                  Submit
                </button>
              </form>
              {feedback && (
                <div className={`mt-4 text-xl font-bold ${feedback.includes('✅') ? 'text-green-600' : 'text-red-600'}`}>
                  {feedback}
                </div>
              )}
            </div>
          )}

          <div className="mt-8 p-4 bg-green-50 rounded-lg text-sm">
            <p><strong>💡 Tips:</strong></p>
            <p>• To find percentage: (part/whole) × 100</p>
            <p>• 50% = 1/2 = 0.5</p>
            <p>• 25% = 1/4 = 0.25</p>
            <p>• 10% = move decimal one place left</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PercentagePower;